import React, { useEffect, useRef, useState } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import useGuardedRealtimeList from '../../../realtime/useGuardedRealtimeList';
import useRealtimeDraft from '../../../realtime/useRealtimeDraft';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Form,
  Spinner,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { normalizeFields } from '../../AdminPage/formManagerUtils';
import InfoBubbleModal from '../../common/InfoBubbleModal';
import SaveBanner from '../Mechanical/SaveBanner';
import {
  buildULForms,
  defaultValue,
  inputType,
  isTrue,
  isFormComplete,
  readFormValues,
  normalizeValues,
  saveULForm,
  valueText,
  writeFormValue,
} from './ulFormUtils';
import './ULForms.css';

const message = (error) =>
  error?.message || 'The request failed. Please try again.';

export function ULField({ field, value, disabled, onChange, onInfo, prefix }) {
  const id = `${prefix}-${field.Id}`;
  const type = inputType(
    field.FieldType ?? field.fieldType,
    field.type || 'text',
  );
  const title = (
    <>
      <span>{field.label}</span>
        <button
          type="button"
          className="ul-forms__info"
          aria-label={`Information about ${field.label}`}
          onClick={() => onInfo(field)}
        >
          <i className="bi bi-info-circle" aria-hidden="true" />
        </button>
    </>
  );
  const choices = type === 'yesna' ? ['Yes', 'N/A'] : ['Yes', 'No'];
  const storedChoice = valueText(value).trim().toLowerCase();
  const selectedChoice = ['true', '1'].includes(storedChoice)
    ? 'yes'
    : type === 'yesno' && ['false', '0'].includes(storedChoice)
      ? 'no'
      : storedChoice;
  return (
    <div className={`ul-forms__field ul-forms__field--${type}`}>
      {['yesna', 'yesno'].includes(type) ? (
        <>
          <div
            className="ul-forms__choices"
            role="group"
            aria-labelledby={`${id}-label`}
          >
            {choices.map((choice) => (
              <Form.Check
                key={choice}
                type="radio"
                id={`${id}-${choice.replace('/', '')}`}
                name={id}
                label={choice}
                disabled={disabled}
                checked={selectedChoice === choice.toLowerCase()}
                onChange={() => onChange(choice)}
              />
            ))}
          </div>
          <div id={`${id}-label`} className="ul-forms__label">
            {title}
          </div>
        </>
      ) : type === 'check' ? (
        <>
          <input
            className="form-check-input"
            id={id}
            type="checkbox"
            disabled={disabled}
            checked={isTrue(value)}
            onChange={(event) => onChange(String(event.target.checked))}
          />
          <label htmlFor={id} className="ul-forms__label">
            {title}
          </label>
        </>
      ) : (
        <>
          <label htmlFor={id} className="ul-forms__label">
            {title}
          </label>
          <Form.Control
            id={id}
            as={type === 'textarea' ? 'textarea' : 'input'}
            rows={type === 'textarea' ? 5 : undefined}
            type={type === 'textarea' ? undefined : type}
            step={type === 'number' ? 'any' : undefined}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
          />
        </>
      )}
    </div>
  );
}

export function ULFormCard({ form, projectId, initialValues, request, url, allowEdit = false }) {
  const [saved, setSaved] = useState(initialValues);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const [lockConfirmed, setLockConfirmed] = useState(false);
  const [info, setInfo] = useState(null);
  const inFlight = useRef(false);
  const saveDelay = useRef(600);
  const mounted = useRef(true);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const active = isTrue(saved[form.activeField?.Id]?.FieldValue);
  const locked =
    lockConfirmed || isTrue(saved[form.lockedField?.Id]?.FieldValue);
  const configured = Boolean(
    form.activeField && form.lockedField && !form.missing.length,
  );
  const dirty = Object.keys(draft).length > 0;
  const incomingValues = useRef(initialValues);
  useRealtimeDraft(dirty || busy || autosaving || uncertain);
  useEffect(() => {
    if (incomingValues.current === initialValues) return;
    incomingValues.current = initialValues;
    if (!dirty && !busy && !autosaving && !uncertain) {
      setSaved(initialValues);
      setLockConfirmed(false);
    }
  }, [initialValues, dirty, busy, autosaving, uncertain]);
  const incomplete = form.requireComplete && !isFormComplete(form, saved, draft);
  const disabled = !allowEdit || busy || locked || uncertain || !configured;
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const snapshot = (values) => {
    if (mounted.current) setSaved(values);
  };
  const act = async (action, automatic = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (automatic) setAutosaving(true);
    else setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (err) {
      if (mounted.current) {
        setError(message(err));
        setUncertain(true);
      }
      try {
        const values = await readFormValues(request, url, projectId);
        if (mounted.current) {
          setSaved(values);
          setUncertain(false);
        }
      } catch (_) {
        /* Keep editing blocked until the server state can be read. */
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setBusy(false);
        setAutosaving(false);
      }
    }
  };

  const activate = () =>
    act(async () => {
      if (!allowEdit) return;
      const current = await readFormValues(request, url, projectId);
      snapshot(current);
      if (isTrue(current[form.lockedField.Id]?.FieldValue))
        throw new Error('This form is locked and cannot be added again.');
      if (!isTrue(current[form.activeField.Id]?.FieldValue))
        await writeFormValue(
          request,
          url,
          projectId,
          form.activeField.Id,
          'true',
          current,
        );
      const updated = await readFormValues(request, url, projectId);
      if (!isTrue(updated[form.activeField.Id]?.FieldValue))
        throw new Error(
          'The form activation could not be verified. Please retry.',
        );
      snapshot(updated);
    });

  const save = (submit, automatic = false) =>
    act(async () => {
      if (!allowEdit) return;
      await saveULForm({
        request,
        url,
        projectId,
        form,
        draft,
        submit,
        onlyDraft: automatic,
        onSnapshot: snapshot,
        onLocked: () => {
          if (mounted.current) setLockConfirmed(true);
        },
      });
      if (mounted.current) {
        setDraft((latest) => {
          const remaining = { ...latest };
          Object.keys(draft).forEach((id) => {
            if (remaining[id] === draft[id]) delete remaining[id];
          });
          return remaining;
        });
        setNotice(
          submit
            ? 'Submitted. This form is now locked.'
            : automatic
              ? 'Changes saved.'
              : 'Draft saved.',
        );
      }
    }, automatic);

  useEffect(() => {
    if (
      !allowEdit ||
      !dirty ||
      busy ||
      autosaving ||
      locked ||
      uncertain ||
      error ||
      !active ||
      !configured
    )
      return undefined;
    const timer = window.setTimeout(() => save(false, true), saveDelay.current);
    return () => window.clearTimeout(timer);
    // Reschedule from the latest draft after each completed save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, busy, autosaving, locked, uncertain, error, active, configured, allowEdit]);

  const refresh = () =>
    act(async () => {
      const values = await readFormValues(request, url, projectId);
      snapshot(values);
      if (mounted.current) {
        setUncertain(false);
        setLockConfirmed(false);
      }
    });

  const change = (field, value) => {
    if (disabled) return;
    saveDelay.current = ['check', 'yesno', 'yesna'].includes(field.type)
      ? 0
      : 600;
    setNotice('');
    setDraft((previous) => {
      const next = { ...previous };
      next[field.Id] = value;
      return next;
    });
  };

  return (
    <article className="ul-forms__card" aria-label={form.title}>
      <div className="ul-forms__card-heading">
        <div>
          <h4>{form.title}</h4>
          {form.subtitle && <p>{form.subtitle}</p>}
        </div>
        <Badge
          bg={locked ? 'secondary' : active ? 'success' : 'light'}
          text={active || locked ? undefined : 'dark'}
        >
          {locked ? 'Locked' : active ? 'In progress' : 'Not added'}
        </Badge>
      </div>
      {!configured && (
        <Alert variant="warning">
          This form is missing{' '}
          {form.missing.length
            ? `${form.missing.length} checklist field(s) or `
            : ''}
          its activation/locking configuration. Complete its fields in Form
          Manager before use.
        </Alert>
      )}
      {error && (
        <Alert variant="danger" role="alert">
          {error}
          <div className="mt-2">
            {uncertain && 'Saved status is unavailable. '}
            <Button size="sm" onClick={refresh} disabled={busy || autosaving}>
              Refresh saved status
            </Button>
          </div>
        </Alert>
      )}
      <div role="status" aria-live="polite">
        <SaveBanner
          showNotification={autosaving || Boolean(notice)}
          saveStatus={autosaving ? 'Saving changes…' : notice}
        />
      </div>
      {!active ? (
        <Button disabled={disabled} onClick={activate}>
          {busy ? 'Adding…' : `Add ${form.title}`}
        </Button>
      ) : (
        <Accordion defaultActiveKey="fields">
          <Accordion.Item eventKey="fields">
            <Accordion.Header>
              {locked ? 'View submitted form' : 'Complete checklist'}
              {dirty && !locked && (
                <span className="ms-2 text-muted">• Unsaved changes</span>
              )}
            </Accordion.Header>
            <Accordion.Body>
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!disabled && !autosaving && !incomplete) save(true);
                }}
              >
                <div className="ul-forms__sheet">
                  <header className="ul-forms__sheet-heading">
                    <h3>{form.title}</h3>
                    {form.subtitle && <p>{form.subtitle}</p>}
                  </header>
                  {form.sections.map((section) => (
                    <section
                      key={section.title}
                      className={`ul-forms__section ul-forms__section--${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      aria-label={section.title}
                    >
                      <h5>{section.title}</h5>
                      {section.fields.some(
                        (field) => field.type === 'yesna',
                      ) && (
                        <p className="ul-forms__instruction">
                          All items listed are either checked Yes or N/A.
                        </p>
                      )}
                      <div
                        className={
                          section.layout === 'grid'
                            ? 'ul-forms__grid'
                            : 'ul-forms__rows'
                        }
                      >
                        {section.fields.map((field) => (
                          <ULField
                            key={field.Id}
                            field={field}
                            value={valueText(
                              locked
                                ? (saved[field.Id]?.FieldValue ??
                                    defaultValue(field))
                                : (draft[field.Id] ??
                                    saved[field.Id]?.FieldValue ??
                                    defaultValue(field)),
                            )}
                            disabled={disabled}
                            onChange={(value) => change(field, value)}
                            onInfo={setInfo}
                            prefix={`ul-${projectId}`}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
                <div className="ul-forms__footer">
                  <span>
                    {locked
                      ? 'Submitted forms cannot be edited.'
                      : 'Changes save automatically. Submit saves all fields and locks this form.'}
                  </span>
                  <div className="d-flex gap-2">
                    <Button type="submit" disabled={disabled || autosaving || incomplete} title={incomplete ? 'Complete every field and check every checklist item before submitting.' : undefined}>
                      {busy ? 'Saving…' : locked ? 'Submitted' : 'Submit'}
                    </Button>
                  </div>
                </div>
              </Form>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
      <InfoBubbleModal
        show={Boolean(info)}
        onHide={() => setInfo(null)}
        sectionName={form.formName}
        value={info?.FieldName || ''}
        title={info?.label || 'Field information'}
      />
    </article>
  );
}

export default function ULForms({ projectId, layouts, category = 'UL' }) {
  const { canView, canEdit } = usePermissions();
  const policy = category === 'Panel Shop' ? 'portal.forms.panel-shop' : 'portal.forms.ul';
  if (!canView(policy)) return null;
  return <ULFormsContent projectId={projectId} layouts={layouts} category={category} allowEdit={canEdit(policy)} />;
}

function ULFormsContent({ projectId, layouts, category, allowEdit }) {
  const urls = useSelector(selectApiUrls);
  const request = usePortalApiRequest();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useGuardedRealtimeList({ apiKey: 'formFields', enabled: Boolean(data && projectId), additionalResources: ['portal:form-parent'],
    apply: fields => setData(previous => previous?.projectId === projectId ? { ...previous, forms: buildULForms(normalizeFields(fields), layouts) } : previous) });
  useGuardedRealtimeList({ apiKey: 'formValues', params: { projectId: Number(projectId) }, enabled: Boolean(data && projectId),
    apply: values => setData(previous => previous?.projectId === projectId ? { ...previous, values: normalizeValues(values, projectId) } : previous) });
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    if (!projectId) return undefined;
    async function load() {
      try {
        if (!urls.formFields || !urls.formValues)
          throw new Error('The form APIs are not configured.');
        const [fields, values] = await Promise.all([
          request({ url: urls.formFields }),
          readFormValues(request, urls.formValues, projectId),
        ]);
        if (!cancelled)
          setData({
            projectId,
            forms: buildULForms(normalizeFields(fields), layouts),
            values,
          });
      } catch (err) {
        if (!cancelled) setError(message(err));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, urls.formFields, urls.formValues, request, attempt, layouts]);
  if (!projectId)
    return <Alert variant="info">Select a job to manage its {category} forms.</Alert>;
  if (error)
    return (
      <Alert variant="danger" role="alert">
        {error}{' '}
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => setAttempt((value) => value + 1)}
        >
          Retry
        </Button>
      </Alert>
    );
  if (!data || String(data.projectId) !== String(projectId))
    return (
      <div role="status">
        <Spinner size="sm" animation="border" /> Loading {category} forms…
      </div>
    );
  return (
    <div className="ul-forms">
      <p className="text-muted">
        Add the checklists needed for this job. Each form is saved and submitted
        separately.
      </p>
      {data.forms.map((form) => (
        <ULFormCard
          key={`${projectId}-${form.id}`}
          form={form}
          projectId={projectId}
          initialValues={data.values}
          allowEdit={allowEdit}
          request={request}
          url={urls.formValues}
        />
      ))}
    </div>
  );
}
